import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { CalendarDays, CalendarPlus, CalendarMinus, Clock, CalendarOff, AlertTriangle, HelpCircle, Trash2, CalendarX2 } from "lucide-react";
import { CallOutDialog } from "@/components/CallOut";

const Section = ({ title, items, color, render }) =>
  items && items.length ? (
    <div>
      <h4 className={`mb-1.5 text-xs font-700 uppercase tracking-wide ${color}`}>{title} ({items.length})</h4>
      <div className="space-y-1">
        {items.map((it, i) => (
          <div key={i} className="rounded-lg border border-zinc-800 bg-zinc-900/50 px-3 py-1.5 text-sm text-zinc-200">{render(it)}</div>
        ))}
      </div>
    </div>
  ) : null;

// Shared "what changed" dialog for schedule imports.
// mode="preview": before an in-app import commits (Apply / Cancel).
// mode="review":  an already-applied background import (Undo / Looks good), may also resolve ambiguous days.
export function ScheduleReviewDialog({ open, onOpenChange, changes, mode, onApply, onUndo, busy }) {
  const c = changes || {};
  const ambiguous = c.ambiguous || [];
  const [picks, setPicks] = useState({});       // {date: chosenTourId}
  const [dels, setDels] = useState({});          // {tourId: true}
  const [coSource, setCoSource] = useState(null); // removed shift → "mark time off"

  useEffect(() => { if (open) { setPicks({}); setDels({}); setCoSource(null); } }, [open, changes]);

  const allPicked = ambiguous.every((a) => picks[a.date]);
  const empty = !(c.added?.length || c.removed?.length || c.retimed?.length || c.timeoff_added?.length || c.timeoff_removed?.length || c.conflicts?.length || ambiguous.length);
  const doApply = () => onApply?.(picks, Object.keys(dels).filter((k) => dels[k]));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg border-zinc-800 bg-zinc-950" data-testid="schedule-review-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display tracking-tight text-cyan-300">
            <CalendarDays className="h-5 w-5" /> {mode === "preview" ? "Review schedule import" : "Recent schedule import"}
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-zinc-400">
          {mode === "preview"
            ? "Here's what this schedule will change. Apply it, or cancel to keep things as they are."
            : "Here's what the latest import changed. You can undo it if it's not right."}
        </p>
        <div className="mt-2 max-h-[55vh] space-y-4 overflow-y-auto" data-testid="schedule-review-list">
          {empty && <p className="text-sm text-zinc-400">No changes — your schedule is already up to date.</p>}

          {ambiguous.length > 0 && (
            <div data-testid="schedule-ambiguous">
              <h4 className="mb-1.5 flex items-center gap-1.5 text-xs font-700 uppercase tracking-wide text-amber-300">
                <HelpCircle className="h-3.5 w-3.5" /> Which tour was scheduled? ({ambiguous.length})
              </h4>
              <div className="space-y-2">
                {ambiguous.map((a) => {
                  if (a.single_request && a.candidates.length === 1) {
                    const cd = a.candidates[0];
                    const linkSel = picks[a.date] === cd.id;
                    const keepSel = picks[a.date] === "__new__" && !dels[cd.id];
                    const delSel = picks[a.date] === "__new__" && dels[cd.id];
                    const choose = (mode) => {
                      if (mode === "link") { setPicks((p) => ({ ...p, [a.date]: cd.id })); setDels((d) => ({ ...d, [cd.id]: false })); }
                      else if (mode === "keep") { setPicks((p) => ({ ...p, [a.date]: "__new__" })); setDels((d) => ({ ...d, [cd.id]: false })); }
                      else { setPicks((p) => ({ ...p, [a.date]: "__new__" })); setDels((d) => ({ ...d, [cd.id]: true })); }
                    };
                    const opt = (sel) => sel ? "border-emerald-500 bg-emerald-500/15 text-emerald-200" : "border-zinc-700 bg-zinc-900 text-zinc-300 hover:bg-zinc-800";
                    return (
                      <div key={a.date} className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-3" data-testid={`ambiguous-${a.date}`}>
                        <p className="mb-2 text-sm text-zinc-200"><b className="font-700">{a.date}</b> already has a future request — <b className="font-700">{cd.title}</b>. Is the tour you just scheduled {a.start ? `(${a.start}–${a.end}) ` : ""}that request?</p>
                        <div className="space-y-1.5">
                          <button type="button" onClick={() => choose("link")} className={`w-full rounded-lg border px-2.5 py-1.5 text-left text-sm ${opt(linkSel)}`} data-testid={`ambiguous-link-${cd.id}`}>
                            Yes — this is that tour
                          </button>
                          <button type="button" onClick={() => choose("keep")} className={`w-full rounded-lg border px-2.5 py-1.5 text-left text-sm ${opt(keepSel)}`} data-testid={`ambiguous-keep-${a.date}`}>
                            No — schedule it & keep the request
                          </button>
                          <button type="button" onClick={() => choose("del")} className={`flex w-full items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-left text-sm ${delSel ? "border-rose-500 bg-rose-500/15 text-rose-300" : "border-zinc-700 bg-zinc-900 text-zinc-300 hover:bg-zinc-800"}`} data-testid={`ambiguous-delete-${cd.id}`}>
                            <Trash2 className="h-3.5 w-3.5" /> Schedule it & delete the request
                          </button>
                        </div>
                      </div>
                    );
                  }
                  return (
                    <div key={a.date} className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-3" data-testid={`ambiguous-${a.date}`}>
                      <p className="mb-2 text-sm text-zinc-200"><b className="font-700">{a.date}</b> has {a.candidates.length} tours — pick the one that was scheduled {a.start ? `(${a.start}–${a.end})` : ""}:</p>
                      <div className="space-y-1.5">
                        {a.candidates.map((cd) => {
                          const chosen = picks[a.date] === cd.id;
                          return (
                            <div key={cd.id} className="flex items-center justify-between gap-2">
                              <button
                                type="button"
                                onClick={() => { setPicks((p) => ({ ...p, [a.date]: cd.id })); setDels((d) => ({ ...d, [cd.id]: false })); }}
                                className={`flex flex-1 items-center gap-2 rounded-lg border px-2.5 py-1.5 text-left text-sm ${chosen ? "border-emerald-500 bg-emerald-500/15 text-emerald-200" : "border-zinc-700 bg-zinc-900 text-zinc-300 hover:bg-zinc-800"}`}
                                data-testid={`ambiguous-pick-${cd.id}`}
                              >
                                <span className={`flex h-4 w-4 items-center justify-center rounded-full border ${chosen ? "border-emerald-400 bg-emerald-500" : "border-zinc-500"}`}>{chosen && <span className="h-1.5 w-1.5 rounded-full bg-white" />}</span>
                                {cd.title}
                              </button>
                              {!chosen && (
                                <button
                                  type="button"
                                  onClick={() => setDels((d) => ({ ...d, [cd.id]: !d[cd.id] }))}
                                  className={`flex items-center gap-1 rounded-lg border px-2 py-1.5 text-xs ${dels[cd.id] ? "border-rose-500 bg-rose-500/15 text-rose-300" : "border-zinc-700 text-zinc-400 hover:bg-zinc-800"}`}
                                  data-testid={`ambiguous-delete-${cd.id}`}
                                  title="Delete this request"
                                >
                                  <Trash2 className="h-3.5 w-3.5" /> {dels[cd.id] ? "Will delete" : "Delete"}
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <Section title="Added" items={c.added} color="text-emerald-300" render={(it) => (
            <span className="flex flex-wrap items-center gap-2"><CalendarPlus className="h-3.5 w-3.5 shrink-0 text-emerald-400" /><b className="font-600">{it.date}</b> {it.label} <span className="text-zinc-400">{it.time}</span>{it.changed && <span className="rounded bg-amber-500/20 px-1.5 text-[10px] font-700 text-amber-300">CHANGED</span>}</span>
          )} />
          {c.removed && c.removed.length > 0 && (
            <div data-testid="schedule-removed">
              <h4 className="mb-1.5 text-xs font-700 uppercase tracking-wide text-rose-300">Removed ({c.removed.length})</h4>
              <p className="mb-1.5 text-xs text-zinc-500">A shift you had is gone from this schedule. If you're off that day, mark it as time off.</p>
              <div className="space-y-1">
                {c.removed.map((it, i) => (
                  <div key={i} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-zinc-800 bg-zinc-900/50 px-3 py-1.5 text-sm text-zinc-200" data-testid={`removed-row-${i}`}>
                    <span className="flex min-w-0 flex-wrap items-center gap-2"><CalendarMinus className="h-3.5 w-3.5 shrink-0 text-rose-400" /><b className="font-600">{it.date}</b> {it.label} <span className="text-zinc-400">{it.time}</span></span>
                    {it.date && (
                      <button
                        type="button"
                        onClick={() => setCoSource({ kind: "shift", label: it.label, date: it.date, hours: it.hours })}
                        className="inline-flex shrink-0 items-center gap-1 rounded-md border border-teal-500/40 bg-teal-500/10 px-2 py-1 text-[11px] font-600 text-teal-300 transition-colors hover:bg-teal-500/20"
                        data-testid={`removed-mark-timeoff-${i}`}
                      >
                        <CalendarX2 className="h-3.5 w-3.5" /> Mark time off
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          <Section title="Tour times adjusted" items={c.retimed} color="text-indigo-300" render={(it) => (
            <span className="flex flex-wrap items-center gap-2"><Clock className="h-3.5 w-3.5 shrink-0 text-indigo-400" /><b className="font-600">{it.date}</b> {it.title} <span className="text-zinc-500 line-through">{it.from}</span>→<span className="text-zinc-200">{it.to}</span></span>
          )} />
          <Section title="Time off added" items={c.timeoff_added} color="text-teal-300" render={(it) => (
            <span className="flex items-center gap-2"><CalendarOff className="h-3.5 w-3.5 shrink-0 text-teal-400" /><b className="font-600">{it.date}</b> {it.type}</span>
          )} />
          <Section title="Time off removed" items={c.timeoff_removed} color="text-zinc-300" render={(it) => (
            <span className="flex items-center gap-2"><CalendarOff className="h-3.5 w-3.5 shrink-0 text-zinc-400" /><b className="font-600">{it.date}</b> {it.type}</span>
          )} />
          <Section title="Kept your detailed tours" items={c.conflicts} color="text-amber-300" render={(it) => (
            <span className="flex items-center gap-2"><AlertTriangle className="h-3.5 w-3.5 shrink-0 text-amber-400" /><b className="font-600">{it.date}</b> {it.name}</span>
          )} />
        </div>
        <div className="mt-3 flex justify-end gap-2">
          {mode === "preview" ? (
            <>
              <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="schedule-review-cancel">Cancel</Button>
              <Button className="bg-[#9B2226] text-white hover:bg-[#7A1A1E]" disabled={busy || !allPicked} onClick={doApply} data-testid="schedule-review-apply">
                {busy ? "Applying…" : (empty ? "Close" : "Apply changes")}
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" onClick={onUndo} data-testid="schedule-review-undo">Undo import</Button>
              {ambiguous.length > 0 ? (
                <Button className="bg-[#9B2226] text-white hover:bg-[#7A1A1E]" disabled={busy || !allPicked} onClick={doApply} data-testid="schedule-review-save">
                  {busy ? "Saving…" : "Save choices"}
                </Button>
              ) : (
                <Button className="bg-zinc-600 text-white hover:bg-zinc-700" onClick={() => onOpenChange(false)} data-testid="schedule-review-ok">Looks good</Button>
              )}
            </>
          )}
        </div>
      </DialogContent>
      <CallOutDialog
        open={!!coSource}
        onOpenChange={(o) => { if (!o) setCoSource(null); }}
        source={coSource}
        onSaved={() => setCoSource(null)}
      />
    </Dialog>
  );
}
